/**
 * ATL-1356 — the Wipon settings selectors, and why they report an outcome rather than a list.
 *
 * Each selector on StoreDetailScreen is seeded from the cached copy Firestore holds
 * (`wiponCredentials.allCashboxes` and its siblings) and refreshed on demand from Wipon. The
 * refresh handlers used to be `if (list) { setState(list) }` with no else, so a refresh that
 * returned nothing left the cached list on screen with no feedback at all — indistinguishable from
 * a successful refresh that happened to return the same thing.
 *
 * Measured on Petit Зенкова (org `n2VI8Md6agsQMFgT5QQQ`, store `kqIOGX2Q80kTxfa5558a`) on
 * 2026-09-18: `allCashboxes` held exactly one entry, `6715:Петит Зенкова`, and the store carried no
 * `accessToken`. Its three siblings share Wipon profile 5319 and the same login, and cache three to
 * five cashboxes each — so the endpoint plainly returns more than one and Atlas filters nothing.
 * The merchant pressed refresh, saw the one stale entry, and concluded Wipon was not offering the
 * replacement cashbox. The two stores missing a token carried the stalest lists, which is the
 * mechanism: with no stored token every refresh depends on a re-login, and when that leg failed the
 * screen said nothing.
 *
 * So the contract here is: a refresh either REPLACES the list or it does not, and the caller is
 * always told which. The distinction matters because the cached list is the only thing standing
 * between the merchant and this exact ticket — a refresh must never quietly reduce it. An empty or
 * malformed answer keeps what is on screen and says so; only a real, non-empty list replaces it.
 */

/** A non-empty list came back. This is the only status that may replace the cached options. */
export const WIPON_REFRESH_OK = 'ok'
/** The session answered, with zero items. Keep the cached list; Wipon simply offered nothing. */
export const WIPON_REFRESH_EMPTY = 'empty'
/** A profile-dependent selector was refreshed before a profile was chosen. Nothing was sent. */
export const WIPON_REFRESH_NO_PROFILE = 'no-profile'
/** Re-authentication itself failed — the stored login or password is the thing to check. */
export const WIPON_REFRESH_AUTH_FAILED = 'auth-failed'
/** The session is good but the request still produced no list. A provider-side problem. */
export const WIPON_REFRESH_UNAVAILABLE = 'unavailable'
/** Something truthy came back that is not a list. Never hand this to `.map`. */
export const WIPON_REFRESH_INVALID = 'invalid'

/**
 * Translation key per non-OK status.
 *
 * Exhaustive by construction, and `wiponRefreshMessageKey` below is what makes that load-bearing:
 * a status with no entry would otherwise reach `t(undefined)` and render an empty error, which is
 * the same silent nothing this ticket is about.
 */
export const WIPON_REFRESH_MESSAGE_KEYS = {
  [WIPON_REFRESH_EMPTY]: 'stores.wiponRefreshEmpty',
  [WIPON_REFRESH_NO_PROFILE]: 'stores.wiponRefreshNoProfile',
  [WIPON_REFRESH_AUTH_FAILED]: 'stores.wiponRefreshError',
  [WIPON_REFRESH_UNAVAILABLE]: 'stores.wiponRefreshUnavailable',
  [WIPON_REFRESH_INVALID]: 'stores.wiponRefreshUnavailable',
}

/** The message key for a non-OK status, falling back to the generic one rather than to nothing. */
export const wiponRefreshMessageKey = (status) =>
  WIPON_REFRESH_MESSAGE_KEYS[status] ?? 'stores.wiponRefreshUnavailable'

/**
 * Load one Wipon settings selector, re-authenticating once when the stored token no longer works.
 *
 * Returns `{status, accessToken?, options?}`. `options` is present only on `WIPON_REFRESH_OK`.
 * `accessToken` is returned whenever a session proved usable — including on `empty`, `unavailable`
 * and `invalid` — because the token belongs with the attempt that produced it: retaining it is what
 * stops the next selector falling back to the same stale session, and what stops Save writing the
 * store back without one.
 */
export const loadWiponOptions = async ({
  accessToken,
  username,
  password,
  selectionCredentials = {},
  requiresProfile = false,
  getAccessToken,
  getOptions,
}) => {
  // Cashboxes, sale points and sale sections are addressed as `/profile/<id>/…`. Without a profile
  // the provider call throws inside its own try/catch and resolves to `undefined`, which used to be
  // indistinguishable from a rejected token — so the screen burned a login round trip and then told
  // the merchant to check a password that was never the problem.
  if (requiresProfile && !selectionCredentials?.profile?.id) {
    return {status: WIPON_REFRESH_NO_PROFILE}
  }

  // The session token goes last so a caller's selection can never shadow it.
  let token = accessToken
  let options = await getOptions({...selectionCredentials, accessToken: token})

  if (!options) {
    token = await getAccessToken({username, password})
    if (!token) return {status: WIPON_REFRESH_AUTH_FAILED}
    options = await getOptions({...selectionCredentials, accessToken: token})
  }

  if (!options) return {status: WIPON_REFRESH_UNAVAILABLE, accessToken: token}
  // A truthy non-array is the shape an HTML error shell or a changed envelope arrives in. Passing
  // it on would throw inside the caller's `.map`, and an unhandled rejection shows no error at all.
  if (!Array.isArray(options)) return {status: WIPON_REFRESH_INVALID, accessToken: token}
  if (options.length === 0) return {status: WIPON_REFRESH_EMPTY, accessToken: token}

  return {status: WIPON_REFRESH_OK, accessToken: token, options}
}

/**
 * Turn a provider list into selector items, dropping anything that has no identity and never
 * emitting `undefined` for a label.
 *
 * Both halves are load-bearing on the save path. `wiponCredentials` is written with a plain
 * `update()` and Firestore runs with `ignoreUndefinedProperties` unset (⇒ false), so a single
 * `text: undefined` inside `allProfiles` rejects the whole document with "Unsupported field value:
 * undefined" — the merchant refreshes successfully, picks the new cashbox, and then cannot save at
 * all. `optionText` reads `value.company?.name` for profiles precisely because that field can be
 * absent, so the `undefined` is reachable from a perfectly healthy response.
 *
 * Falling back to the id is not a cosmetic default: the id is the identity Wipon fiscalizes
 * against, and an entry the merchant can see and select beats an entry that blocks every save.
 */
export const toWiponSelectorItems = (options, optionText) =>
  options
    .filter(value => value !== null && typeof value === 'object' && value.id !== null && value.id !== undefined)
    .map(value => {
      const text = optionText(value)
      return {id: value.id, text: typeof text === 'string' && text.length > 0 ? text : String(value.id)}
    })

/**
 * Build the nested map written by StoreDetailScreen without dropping the live session.
 *
 * It emits the twelve keys the screen holds as state, which is every key present on the 22 live
 * `wipon-kassa` stores (measured 2026-09-18). `WiponCredentialsDto` also declares three legacy
 * fields — `login`, `terminalId`, `cashRegisterId` — that no live store carries; they are not
 * spread through here, so if one ever appears this builder would drop it.
 */
export const buildWiponCredentials = ({
  username,
  password,
  accessToken,
  profile,
  cashbox,
  allProfiles,
  allCashboxes,
  salePoint,
  allSalePoints,
  saleSection,
  allSaleSections,
  vatType,
}) => ({
  username,
  password,
  ...(accessToken ? {accessToken} : {}),
  profile,
  cashbox,
  allProfiles,
  allCashboxes,
  salePoint,
  allSalePoints,
  saleSection,
  allSaleSections,
  vatType,
})
